import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";

import ArrowBack from "@mui/icons-material/ArrowBack";

import { useUser } from "../../contexts/UserContext";
import AdminLayout from "../layout/AdminLayout";
import { useApi } from "../../hooks/useApi";

import { InternalResponsiveColumn } from "../common/Common.elements";
import { useFormData } from "../../hooks/useFormData";
import UserSelect from "./components/UserSelect";
import {
  assembleJobAlertSms,
  fitsDraftForSend,
  segmentInfo,
  toGsm7,
  WORST_CASE_FIRST_NAME,
} from "../../lib/smsSegment";

const DEFAULT_VALUES = {
  neighborhood: "",
  city: "Nashville",
  state: "TN",
  date: "",
  rate: 15,
  helpNeeded: "",
  smsMessage: "",
  recipientMode: "all",
  selectedHelpers: [],
};

const AdminJobAlert = () => {
  const { formData, onChange, setData } = useFormData({ ...DEFAULT_VALUES });
  const { user, signOut } = useUser();
  const navigate = useNavigate();
  const [emailPreview, setEmailPreview] = useState(null);

  const { call: callApi, isLoading: isSendingAlert } = useApi({
    onError: (err) => toast.error(err.message),
  });

  const { call: callPreview, isLoading: isPreviewing } = useApi({
    onError: (err) => toast.error(err.message),
  });

  const { call: callGenerateSms, isLoading: isGeneratingSms } = useApi({
    onError: (err) => toast.error(err.message),
  });

  const selectedHelpers = formData.selectedHelpers || [];
  const needsHelpers =
    formData.recipientMode === "include" ||
    formData.recipientMode === "exclude";

  const jobFieldsReady =
    !!formData.neighborhood &&
    !!formData.city &&
    !!formData.state &&
    !!formData.date &&
    !!formData.rate &&
    !!formData.helpNeeded;

  const smsMessage = formData.smsMessage || "";
  const smsPreviewSample = assembleJobAlertSms(smsMessage, "Alex");
  const smsFitsLimit = !smsMessage.trim() || fitsDraftForSend(smsMessage);
  const smsLimitInfo = segmentInfo(
    assembleJobAlertSms(smsMessage, WORST_CASE_FIRST_NAME)
  );

  const contentPayload = () => ({
    neighborhood: formData.neighborhood,
    city: formData.city,
    state: formData.state,
    date: formData.date,
    rate: formData.rate,
    helpNeeded: formData.helpNeeded,
  });

  const sendJobAlert = (payload) => {
    if (!isSendingAlert) {
      callApi(`/admin/job-requests/alert`, "post", undefined, payload, (res) => {
        const count = res?.recipientCount;
        toast.success(
          typeof count === "number"
            ? `Your alert has been successfully sent to ${count} helper${
                count === 1 ? "" : "s"
              }.`
            : "Your alert has been successfully sent."
        );
        setData({ ...DEFAULT_VALUES });
        setEmailPreview(null);
      });
    }
  };

  useEffect(() => {
    if (user.userType !== "admin") {
      signOut();
      navigate("/sign-in");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onPreviewEmail = () => {
    if (!jobFieldsReady || isPreviewing) {
      return;
    }
    callPreview(
      `/admin/job-requests/alert/preview`,
      "post",
      undefined,
      contentPayload(),
      (res) => {
        setEmailPreview({
          subject: res.subject,
          html: res.html,
        });
      }
    );
  };

  const onGenerateSms = () => {
    if (!jobFieldsReady || isGeneratingSms) {
      return;
    }
    callGenerateSms(
      `/admin/job-requests/alert/generate-sms`,
      "post",
      undefined,
      contentPayload(),
      (res) => {
        onChange({
          target: {
            name: "smsMessage",
            value: toGsm7(res.smsMessage || ""),
          },
        });
      }
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!fitsDraftForSend(smsMessage)) {
      toast.error("SMS is over the character limit. Shorten the message.");
      return;
    }

    const basePayload = {
      ...contentPayload(),
      smsMessage: smsMessage.trim(),
      recipientMode: formData.recipientMode || "all",
    };

    if (needsHelpers) {
      basePayload.helperIds = selectedHelpers.map((h) => h.value);
    }

    sendJobAlert(basePayload);
  };

  const render = () => {
    return (
      <form onSubmit={onSubmit}>
        <Row>
          <Col xs={12} md={6} lg={4}>
            <Form.Group className="mb-3">
              <Form.Label>Neighborhood *</Form.Label>
              <Form.Control
                type="text"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={onChange}
                required={true}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Form.Group className="mb-3">
              <Form.Label>City *</Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={formData.city}
                onChange={onChange}
                required={true}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Form.Group className="mb-3">
              <Form.Label>State *</Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={formData.state}
                onChange={onChange}
                required={true}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label>Date/Time *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="date"
                value={formData.date}
                onChange={onChange}
                required={true}
                placeholder={`Example:
3x Weekly Shifts Available
- Fridays from 3:30 to 8:30pm
- Saturdays from 8:00am to 1:00pm
- Saturdays from 3:30 to 8:30pm
`}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label>Hourly Rate *</Form.Label>
              <Form.Control
                type="number"
                name="rate"
                value={formData.rate}
                onChange={onChange}
                required={true}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label>Help Needed *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="helpNeeded"
                value={formData.helpNeeded}
                onChange={onChange}
                required={true}
                placeholder={`Example:
Customer needs help two days a week with meal prep and personal care. Customer uses a walker and a gait belt for transport.
`}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">SMS Message *</Form.Label>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  disabled={!jobFieldsReady || isGeneratingSms}
                  onClick={onGenerateSms}
                >
                  {isGeneratingSms ? "Generating…" : "Generate SMS"}
                </Button>
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                name="smsMessage"
                value={smsMessage}
                onChange={(e) =>
                  onChange({
                    target: {
                      name: "smsMessage",
                      value: toGsm7(e.target.value),
                    },
                  })
                }
                required={true}
                isInvalid={!!smsMessage.trim() && !smsFitsLimit}
                placeholder="Generate a short SMS. Reply number and STOP are added automatically. Use {{firstName}} to personalize."
              />
              <Form.Text className="text-muted d-block">
                Character limit: {smsLimitInfo.units}/{smsLimitInfo.limit}
                {!smsFitsLimit ? " — shorten the message" : ""}. Reply number
                and STOP are included. Use <code>{"{{firstName}}"}</code> to
                personalize.
              </Form.Text>
              {smsMessage ? (
                <Form.Text className="d-block mt-1">
                  Preview: {smsPreviewSample}
                </Form.Text>
              ) : null}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col xs={12}>
            <Button
              type="button"
              variant="outline-primary"
              disabled={!jobFieldsReady || isPreviewing}
              onClick={onPreviewEmail}
            >
              {isPreviewing ? "Loading preview…" : "Preview email"}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label>Recipients</Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  id="recipientMode-all"
                  name="recipientMode"
                  label="All active helpers"
                  value="all"
                  checked={formData.recipientMode === "all"}
                  onChange={onChange}
                />
                <Form.Check
                  type="radio"
                  id="recipientMode-include"
                  name="recipientMode"
                  label="Only selected helpers"
                  value="include"
                  checked={formData.recipientMode === "include"}
                  onChange={onChange}
                />
                <Form.Check
                  type="radio"
                  id="recipientMode-exclude"
                  name="recipientMode"
                  label="All active helpers except selected"
                  value="exclude"
                  checked={formData.recipientMode === "exclude"}
                  onChange={onChange}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        {needsHelpers && (
          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {formData.recipientMode === "include"
                    ? "Helpers to include *"
                    : "Helpers to exclude"}
                </Form.Label>
                <UserSelect
                  value={selectedHelpers}
                  onChange={(vals) =>
                    onChange({
                      target: {
                        name: "selectedHelpers",
                        value: vals || [],
                      },
                    })
                  }
                  isMulti={true}
                  userType="helper"
                  showDefaultOptions={true}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          style={{ minWidth: 200 }}
          disabled={
            isSendingAlert ||
            !jobFieldsReady ||
            !smsMessage.trim() ||
            !smsFitsLimit ||
            (formData.recipientMode === "include" &&
              selectedHelpers.length === 0)
          }
          className="w-100"
        >
          Send Alert
        </Button>
      </form>
    );
  };

  const renderPreviewModal = () => (
    <Modal
      show={!!emailPreview}
      onHide={() => setEmailPreview(null)}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Email preview</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {emailPreview ? (
          <>
            <div className="mb-3">
              <strong>Subject:</strong> {emailPreview.subject}
            </div>
            <iframe
              title="Email preview"
              srcDoc={emailPreview.html}
              sandbox=""
              style={{
                width: "100%",
                minHeight: "70vh",
                border: "1px solid #dee2e6",
                background: "#fff",
              }}
            />
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );

  return (
    <AdminLayout>
      <Helmet>
        <title>Create Job Alert | Manana</title>
        <meta name="description" content="Manana Help" />
      </Helmet>
      <Container fluid>
        <Row>
          <InternalResponsiveColumn>
            <Link to="/admin">
              <ArrowBack />
            </Link>
            <Row>
              <Col>
                <h2 className="mb-5">
                  New Job Alert
                </h2>
              </Col>
            </Row>
            <hr />
            {render()}
            {renderPreviewModal()}
          </InternalResponsiveColumn>
        </Row>
      </Container>
    </AdminLayout>
  );
};

export default AdminJobAlert;
